import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Plane, MapPin } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Search-as-you-type airport picker.
 * Stores value as a formatted string like "LKO · Lucknow" for backwards
 * compatibility with the existing quotation schema and PDF renderers.
 */
export default function AirportSelect({ label, value, onChange, testid, placeholder = "Type city, name or IATA" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(0);
  const debRef = useRef();

  // Fetch results (debounced) whenever query changes and dropdown is open
  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API}/airports/search`, {
          params: { q: query, limit: 12 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setResults(Array.isArray(data) ? data : []);
        setHover(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(debRef.current);
  }, [q, open]);

  const displayLabel = useMemo(() => value || "", [value]);

  const pick = (a) => {
    onChange(`${a.iata} · ${a.city}`);
    setOpen(false);
    setQ("");
  };

  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full border border-border rounded px-3 py-2 text-sm text-left flex items-center gap-2 bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            <Plane size={14} className="text-slate-400 shrink-0" />
            <span className={displayLabel ? "text-slate-900 truncate" : "text-slate-400 truncate"}>
              {displayLabel || placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              data-testid={`${testid}-input`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => Math.min(h + 1, results.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
                else if (e.key === "Enter" && results[hover]) { e.preventDefault(); pick(results[hover]); }
                else if (e.key === "Escape") { setOpen(false); }
              }}
              placeholder="Type city, name or IATA (e.g. LKO)"
              className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
            {!loading && q.trim().length < 2 && (
              <div className="px-3 py-2 text-xs text-slate-400">Type at least 2 characters</div>
            )}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">No airports found</div>
            )}
            {results.map((a, i) => (
              <button
                key={a.iata}
                type="button"
                data-testid={`${testid}-option-${a.iata}`}
                onMouseEnter={() => setHover(i)}
                onClick={() => pick(a)}
                className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-slate-50 last:border-b-0 ${
                  i === hover ? "bg-emerald-50" : "hover:bg-slate-50"
                }`}
              >
                <span className="inline-flex items-center justify-center w-11 h-6 rounded bg-slate-900 text-white text-[10px] font-semibold tracking-widest shrink-0 mt-0.5">
                  {a.iata}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-900 truncate">{a.name}</span>
                  <span className="block text-xs text-slate-500 truncate flex items-center gap-1">
                    <MapPin size={10} /> {a.city}{a.country ? `, ${a.country}` : ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
